import { useState, useEffect, useCallback } from 'react';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { AModal, ASwitch, AInput, ARadioButton } from '../../../components';
import { Col, Form, Row } from 'antd';
import { useToast } from '../../../hooks/toast';
import { schemaTaxe } from '../schemas/yup.taxe.schema';
import { ITaxe } from '../../../shared/interfaces';
import { patchTaxe, postTaxe } from '../../../http';

interface ITaxesProps {
  open: boolean;
  close: () => void;
  taxe: ITaxe | null;
  setTaxes: React.Dispatch<React.SetStateAction<ITaxe[]>>;
  payment_method_id: string | undefined;
}

const TaxeCreateEdit: React.FC<ITaxesProps> = ({
  open,
  close,
  taxe,
  setTaxes,
  payment_method_id,
}) => {
  const { addToast } = useToast();
  const [description, setDescription] = useState<string>('');
  const [type, setType] = useState<number>(0);
  const [initialInstallment, setInitialInstallment] = useState<number>(1);
  const [finalInstallment, setFinalInstallment] = useState<number>(1);
  const [value, setValue] = useState<number>(0);
  const [status, setStatus] = useState<boolean>(false);

  const handleCreate = useCallback(async () => {
    schemaTaxe
      .validate({
        description,
        payment_method_id,
        type,
        initial_installment: initialInstallment,
        final_installment: finalInstallment,
        value,
      })
      .then(async (validated) => {
        await postTaxe(validated)
          .then((data) => {
            setTaxes((state) => [...state, data]);
            addToast({
              type: 'success',
              title: 'Cadastrado',
              description: 'Cadastrado com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao cadastrar.',
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      });
  }, [description, initialInstallment, finalInstallment, type, value]);

  const handleEdit = useCallback(async () => {
    schemaTaxe
      .validate({
        description,
        payment_method_id,
        type,
        initial_installment: initialInstallment,
        final_installment: finalInstallment,
        value,
        status,
      })
      .then(async (validated) => {
        await patchTaxe(taxe?.id, validated)
          .then((data) => {
            setTaxes((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: 'Alterado',
              description: 'Dados alterados com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao alterar.',
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      });
  }, [description, initialInstallment, finalInstallment, type, value, status]);

  useEffect(() => {
    if (taxe) {
      setDescription(taxe.description);
      setInitialInstallment(taxe.initial_installment);
      setFinalInstallment(taxe.final_installment);
      setType(taxe.type);
      setValue(taxe.value);
      setStatus(!!taxe.status);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        taxe ? (
          <>
            <EditOutlined /> Editar
          </>
        ) : (
          <>
            <FormOutlined /> Cadastrar
          </>
        )
      }
      onOk={taxe ? handleEdit : handleCreate}
    >
      <Form layout="vertical">
        <Form.Item label="Descrição" required>
          <AInput
            placeholder="Ex. Parcelamento de 2x à 6 x"
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>
        <Row>
          <Col span={12}>
            <Form.Item label="De parcela" required>
              <AInput
                placeholder="Informe a parcela inicial"
                name="initial_installment"
                type="number"
                value={initialInstallment}
                onChange={(e) => setInitialInstallment(+e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Até Parcela" required>
              <AInput
                placeholder="Informe a parcela final"
                name="final_installment"
                value={finalInstallment}
                onChange={(e) => setFinalInstallment(+e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Tipo da taxa" required>
          <ARadioButton
            value={type}
            name="type"
            onChange={(e) => setType(+e.target.value)}
            options={[
              { label: 'Porcentagem', value: 0 },
              { label: 'Valor', value: 1 },
            ]}
          />
        </Form.Item>
        <Form.Item label="Valor" required>
          <AInput
            placeholder="Informe valor da taxa"
            name="value"
            type="number"
            value={value}
            onChange={(e) => setValue(+e.target.value)}
          />
        </Form.Item>
        {taxe && (
          <Form.Item label="Ativo">
            <ASwitch
              checked={status}
              checkedChildren="Sim"
              unCheckedChildren="Não"
              onChange={() => {
                setStatus(!status);
              }}
            />
          </Form.Item>
        )}
      </Form>
    </AModal>
  );
};

export default TaxeCreateEdit;
